import { getCustomTag, sendNotification, useApiClient, useTranslation } from '@msdk/common'
import {
  CustomActionButtonAspect,
  DefaultActionType,
  Entity,
  OptionsetValue,
  ServerOperationResult,
} from '@msdk/definitions'
import React from 'react'

import { useComponentModal } from '../../../hooks'
import { messagesFromEntityOperation } from '../../../utils'
import { ActionConfirmationModal } from '../../ActionConfirmationModal'
import { ActionVisualizationMode } from '../../FormActions/action-visualization'
import { ActionElement } from '../../FormActions/ActionElement'
import { useDynamicViewContext } from '../context'
import { ActionLabels, getActivateActionLabels } from './labels-utils'

interface Props {
  row: Entity
  visualizationMode: ActionVisualizationMode
  onClose?: () => void
}

export function ActivateRowAction({ row, visualizationMode, onClose }: Props) {
  const { entityModel, baseMetadata, reload } = useDynamicViewContext()

  const actionDetails = entityModel.defaultActionsDetails.find((e) => e.type === DefaultActionType.Activate)

  const api = useApiClient()
  const t = useTranslation()
  const { createModal } = useComponentModal()

  const labels: ActionLabels = getActivateActionLabels(actionDetails, t)

  const handleClick = async () => {
    onClose && onClose()

    const onConfirm = async () => {
      const cloned = { ...row }
      const statusField = new OptionsetValue('ACTIVE', 'ENTITY_STATUS')
      cloned.fields = {}
      cloned.fields['ENTITY_STATUS'] = statusField
      cloned.fields['DEACTIVATION_REASON'] = null

      if (entityModel.extendedBy != null && entityModel.extendedBy != '') {
        cloned.fields['EXTENDED_BY'] = row.fields['EXTENDED_BY']
        cloned.fields['EXTENDED_BY_PARENTID'] = row.fields['EXTENDED_BY_PARENTID']
      }

      const res = await api.entity.forceSaveEntities(baseMetadata.msProvider, entityModel, [cloned])
      const serverMessages = messagesFromEntityOperation(res)
      if (serverMessages.length) {
        const translated = serverMessages.map((msg) => `${t(msg.idml, msg.description)}`).join('\n')
        sendNotification({ message: translated, status: 'error' })
      } else {
        if (res.result === ServerOperationResult.Success) {
          sendNotification({ message: labels.success, status: 'success' })
          reload()
        } else {
          sendNotification({ message: labels.error, status: 'error' })
        }
      }
    }

    const modalRef = createModal(
      <ActionConfirmationModal
        title={labels.title}
        body={labels.body}
        onConfirm={onConfirm}
        confirmationLabel={labels.confirmationLabel}
        type={DefaultActionType.Activate}
      />,
      () => {
        //do nothing
      },
    )

    modalRef.open()
  }

  return (
    <ActionElement
      icon={labels.iconTag}
      label={labels.description}
      onClick={handleClick}
      visualizationMode={visualizationMode}
      disabled={false}
      testId={getCustomTag(['FORM', 'ACTIVATE', 'button'])}
      aspect={CustomActionButtonAspect.Primary}
    />
  )
}
